import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { Balance, ChevronRight } from '@mui/icons-material';

import { CardSkeleton } from './CardSkeleton';

interface SimilarCasesCardProps {
  similarCasesData: {
    count: number;
    results: any[];
  };
  isLoading: boolean;
}
export const SimilarCasesCard = ({
  similarCasesData,
  isLoading,
}: SimilarCasesCardProps) => {
  const constants = useAppConstantsContext();
  const { isMobile } = useScreenSize();
  const handleViewClick = (result: any) => {
    const similarCasesUrl = `${constants.links.baseUrl}/dashboard/court-cases/opinions/${result.id}`;
    window.open(similarCasesUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      {isLoading ? (
        <CardSkeleton />
      ) : (
        similarCasesData.results.map((result: any, index: number) => (
          <Box key={`similar-case-${index}`}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="start"
            >
              <Stack direction="row" spacing={2}>
                <Box
                  borderRadius="100%"
                  sx={{
                    backgroundColor: 'primary.light',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Balance color="primary" />
                </Box>
                <Stack>
                  {result.caseName && (
                    <Text variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {result.caseName}
                    </Text>
                  )}
                  {(result.dateFiled || result.dateTerminated) && (
                    <Box display="flex" gap={1} sx={{ mb: 1 }}>
                      {result.dateFiled && (
                        <Text variant="body2" sx={{ fontWeight: 400 }}>
                          <span style={{ fontWeight: 600 }}>Filed:</span>{' '}
                          {new Date(result.dateFiled).toLocaleDateString()}
                        </Text>
                      )}
                      {result.dateFiled && result.dateTerminated && (
                        <Text component="span" sx={{ fontWeight: 600 }}>
                          {' '}
                          |{' '}
                        </Text>
                      )}
                      {result.dateTerminated && (
                        <Text variant="body2" sx={{ fontWeight: 400 }}>
                          <span style={{ fontWeight: 600 }}>Terminated:</span>{' '}
                          {new Date(result.dateTerminated).toLocaleDateString()}
                        </Text>
                      )}
                    </Box>
                  )}
                  {result.docketNumber && (
                    <Text
                      variant="body2"
                      color={'text.secondary'}
                      sx={{ fontWeight: 400, mb: 1 }}
                    >
                      <span style={{ fontWeight: 600 }}>Docket:</span>{' '}
                      {result.docketNumber}
                    </Text>
                  )}
                  {result.court && (
                    <Text
                      variant="body2"
                      color={'text.secondary'}
                      sx={{ fontWeight: 400, mb: 1 }}
                    >
                      <span style={{ fontWeight: 600 }}>Court:</span>{' '}
                      {result.court}
                    </Text>
                  )}
                  {result.suitNature && (
                    <Text
                      variant="body2"
                      color={'text.secondary'}
                      sx={{ fontWeight: 400, mb: 1 }}
                    >
                      <span style={{ fontWeight: 600 }}>Nature of Suit:</span>{' '}
                      {result.suitNature}
                    </Text>
                  )}
                  {result.party &&
                    Array.isArray(result.party) &&
                    result.party.length > 0 && (
                      <Text
                        variant="body2"
                        color={'text.secondary'}
                        sx={{ fontWeight: 400, mb: 1 }}
                      >
                        <span style={{ fontWeight: 600 }}>Party:</span>{' '}
                        {result.party.slice(0, 3).join(', ')}
                        {result.party.length > 3 &&
                          `, +${result.party.length - 3} more`}
                      </Text>
                    )}
                  {result.attorney &&
                    Array.isArray(result.attorney) &&
                    result.attorney.length > 0 && (
                      <Text
                        variant="body2"
                        color={'text.secondary'}
                        sx={{ fontWeight: 400, mb: 1 }}
                      >
                        <span style={{ fontWeight: 600 }}>Attorney:</span>{' '}
                        {result.attorney.slice(0, 3).join(', ')}
                        {result.attorney.length > 3 &&
                          `, +${result.attorney.length - 3} more`}
                      </Text>
                    )}
                </Stack>
              </Stack>
              {isMobile ? (
                <IconButton
                  onClick={() => handleViewClick(result)}
                  sx={{ p: 0.5 }}
                  aria-label="View case"
                >
                  <ChevronRight />
                </IconButton>
              ) : (
                <Button color="primary" onClick={() => handleViewClick(result)}>
                  View
                </Button>
              )}
            </Stack>
            {index < similarCasesData.results.length - 1 && (
              <Divider sx={{ my: 2 }} />
            )}
          </Box>
        ))
      )}
    </Box>
  );
};
